import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformReturnDisposalIssue } from '~/utils/receive/transform';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeReturnDisposalInfo,
  TypeReturnDisposalListParams,
  TypeReturnDisposalListResponse,
} from '~/types/receive.types';

type TypeReturnDisposalListResult = {
  list: TypeReturnDisposalInfo[];
  totalCount: number;
};

/** [입고관리 > 회송/폐기 관리] 목록 조회 API */
const fetchReturnDisposalList = async (params: TypeReturnDisposalListParams): Promise<TypeReturnDisposalListResult> => {
  const { data } = await axiosInstance.get<TypeReturnDisposalListResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES,
    { params }
  );

  return {
    list: data.data.issues.map(transformReturnDisposalIssue),
    totalCount: data.pagination.listTotal,
  };
};

type TypeUseGetReturnDisposalListQueryParams = {
  params: TypeReturnDisposalListParams;
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 목록 조회 Query Hook */
export const useGetReturnDisposalListQuery = ({ params, enabled = true }: TypeUseGetReturnDisposalListQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalIssues(params),
    queryFn: () => fetchReturnDisposalList(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

/** [입고관리 > 회송/폐기 관리] 목록 조회 Mock (파라미터 무시·시나리오 고정 응답) */
registerMockAPI<TypeReturnDisposalListResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES,
  label: '회송폐기관리/목록 조회',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          issues: [
            {
              inspectionId: 1,
              purchaseOrderCode: 'RO202606020001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-06-02T15:10:00',
              elapsedDays: 28,
              inspectionProcessMethod: 'APPLICATION_AWAITING',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB1111111115',
              goodsName: '한글표시 누락 케이스 - 견과류 스낵',
              issueType: '한글표시사항 누락',
              defectSku: 2,
              defectQty: 20,
              scrapPlannedAt: '2026-06-17T15:10:00',
              rtsPlannedAt: null,
            },
            {
              inspectionId: 2,
              purchaseOrderCode: 'RO202606100012',
              issuedWarehouse: 'GGH1',
              issuedWarehouseName: '곤지암냉장',
              issueRegisteredAt: '2026-06-10T09:32:00',
              elapsedDays: 20,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'IN_PROGRESS',
              goodsCode: 'HB2233445566',
              goodsName: '유리병 파손 케이스 - 프리미엄 올리브유 500ml',
              issueType: '파손',
              defectSku: 1,
              defectQty: 6,
              scrapPlannedAt: null,
              rtsPlannedAt: null,
            },
            {
              inspectionId: 3,
              purchaseOrderCode: 'RO202606120033',
              issuedWarehouse: 'GGM1',
              issuedWarehouseName: '곤지암상온',
              issueRegisteredAt: '2026-06-12T11:05:00',
              elapsedDays: 18,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'COMPLETED',
              goodsCode: 'PD7788990011',
              goodsName: '유통기한 임박 - 저지방 우유 1L',
              issueType: '유통기한 임박',
              defectSku: 3,
              defectQty: 45,
              scrapPlannedAt: null,
              rtsPlannedAt: null,
            },
            {
              inspectionId: 4,
              purchaseOrderCode: 'RO202606150041',
              issuedWarehouse: 'GPL1',
              issuedWarehouseName: '김포냉동',
              issueRegisteredAt: '2026-06-15T17:48:00',
              elapsedDays: 15,
              inspectionProcessMethod: 'SCRAP',
              inspectionProcessStatus: 'IN_PROGRESS',
              goodsCode: 'HB3344556677',
              goodsName: '이물질 혼입 - 냉동 만두 1kg',
              issueType: '이물질 혼입',
              defectSku: 1,
              defectQty: 12,
              scrapPlannedAt: '2026-06-30T17:48:00',
              rtsPlannedAt: null,
            },
            {
              inspectionId: 5,
              purchaseOrderCode: 'RO202606180052',
              issuedWarehouse: 'KCH1',
              issuedWarehouseName: '화도냉장',
              issueRegisteredAt: '2026-06-18T08:20:00',
              elapsedDays: 12,
              inspectionProcessMethod: 'SCRAP',
              inspectionProcessStatus: 'COMPLETED',
              goodsCode: 'PD9900112233',
              goodsName: '유통기한 경과 - 수제 요거트 4입',
              issueType: '유통기한 경과',
              defectSku: 2,
              defectQty: 8,
              scrapPlannedAt: '2026-06-25T08:20:00',
              rtsPlannedAt: null,
            },
            {
              inspectionId: 6,
              purchaseOrderCode: 'RO202606200063',
              issuedWarehouse: 'GPH1',
              issuedWarehouseName: '평택상온 입고대행_김포노선_상온',
              issueRegisteredAt: '2026-06-20T13:14:00',
              elapsedDays: 10,
              inspectionProcessMethod: 'CLAIM_REQUEST',
              inspectionProcessStatus: 'CLAIM_REQUESTED',
              goodsCode: 'HB4455667788',
              goodsName: '수량 불일치 - 유기농 라면 멀티팩',
              issueType: '수량 불일치',
              defectSku: 1,
              defectQty: 30,
              scrapPlannedAt: null,
              rtsPlannedAt: null,
            },
            {
              inspectionId: 7,
              purchaseOrderCode: 'RO202606220074',
              issuedWarehouse: 'GGL1',
              issuedWarehouseName: '곤지암냉동',
              issueRegisteredAt: '2026-06-22T16:02:00',
              elapsedDays: 8,
              inspectionProcessMethod: 'HELD',
              inspectionProcessStatus: 'HELD',
              goodsCode: 'PD5566778899',
              goodsName: '라벨 오부착 - 냉동 블루베리 500g',
              issueType: '라벨 오부착',
              defectSku: 2,
              defectQty: 16,
              scrapPlannedAt: null,
              rtsPlannedAt: null,
            },
            {
              // 재입고(대기): 재발주 후 입고를 기다리는 카드 — 선택은 가능하나 전환 매트릭스에서 3종 전부 차단
              inspectionId: 8,
              purchaseOrderCode: 'RO202606240085',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-06-24T10:41:00',
              elapsedDays: 6,
              inspectionProcessMethod: 'REPLACEMENT',
              inspectionProcessStatus: 'WAITING',
              goodsCode: 'HB6677889900',
              goodsName: '포장 불량 - 프리미엄 사과즙 30포',
              issueType: '포장 불량',
              defectSku: 1,
              defectQty: 5,
              scrapPlannedAt: null,
              rtsPlannedAt: null,
            },
            {
              // 보류해제-회송: 컬리가 회수예정일을 지정해 회수 신청 시 날짜 변경이 불가한 카드.
              // 경과일수는 클레임 기한(D+3) 이내로 둔다 — 클레임 버튼 비활성의 원인이 기한 초과가 아니라
              // 전환 매트릭스(회송(정보입력대기) 클레임 X)임을 e2e에서 특정할 수 있어야 한다.
              inspectionId: 9,
              purchaseOrderCode: 'RO202606260096',
              issuedWarehouse: 'KCM1',
              issuedWarehouseName: '화도상온',
              issueRegisteredAt: '2026-06-26T19:27:00',
              elapsedDays: 2,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'PD1122334455',
              goodsName: '오염 - 신선 샐러드 채소 모둠',
              issueType: '오염',
              defectSku: 4,
              defectQty: 24,
              scrapPlannedAt: '2026-07-11T19:27:00',
              rtsPlannedAt: '2026-08-21T09:00:00',
            },
            {
              inspectionId: 10,
              purchaseOrderCode: 'RO202606280107',
              issuedWarehouse: 'TP01',
              issuedWarehouseName: '평택상온',
              issueRegisteredAt: '2026-06-28T07:55:00',
              elapsedDays: 2,
              inspectionProcessMethod: 'APPLICATION_AWAITING',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB2200110099',
              goodsName: '규격 상이 - 대용량 키친타올 12롤',
              issueType: '규격 상이',
              defectSku: 1,
              defectQty: 3,
              scrapPlannedAt: '2026-07-13T07:55:00',
              rtsPlannedAt: null,
            },
          ],
        },
        pagination: {
          listView: 50,
          listTotal: 10,
          cursor: 0,
        },
      },
    },
    {
      // 보류해제-회송(RTS+PENDING_INPUT) 동일 지정일 2건 + 동일 발주·센터의 일반 신청대기 1건(혼합 차단 검증용)
      // + 지정일이 다른 보류해제-회송 1건(회송예정일 상이 차단 검증용)
      name: '보류해제-회송 포함',
      response: {
        data: {
          issues: [
            {
              inspectionId: 101,
              purchaseOrderCode: 'RO202608010001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-08-01T10:00:00',
              elapsedDays: 17,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566778899',
              goodsName: '보류해제-회송 케이스 - 냉동 만두 1kg',
              issueType: '파손',
              defectSku: 1,
              defectQty: 10,
              scrapPlannedAt: '2026-08-22T00:00:00',
              rtsPlannedAt: '2026-08-21T09:00:00',
            },
            {
              inspectionId: 102,
              purchaseOrderCode: 'RO202608010001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-08-01T10:00:00',
              elapsedDays: 17,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566778900',
              goodsName: '보류해제-회송 케이스 - 냉동 교자 800g',
              issueType: '파손',
              defectSku: 1,
              defectQty: 5,
              scrapPlannedAt: '2026-08-22T00:00:00',
              rtsPlannedAt: '2026-08-21T09:00:00',
            },
            {
              inspectionId: 103,
              purchaseOrderCode: 'RO202608010001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-08-14T09:00:00',
              elapsedDays: 4,
              inspectionProcessMethod: 'APPLICATION_AWAITING',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566778901',
              goodsName: '일반 신청대기 케이스 - 냉동 핫도그 500g',
              issueType: '오염',
              defectSku: 1,
              defectQty: 8,
              scrapPlannedAt: '2026-08-29T09:00:00',
              rtsPlannedAt: null,
            },
            {
              inspectionId: 104,
              purchaseOrderCode: 'RO202608010001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              issueRegisteredAt: '2026-08-02T10:00:00',
              elapsedDays: 16,
              inspectionProcessMethod: 'RTS',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566778902',
              goodsName: '보류해제-회송 지정일 상이 케이스 - 냉동 볶음밥 1kg',
              issueType: '파손',
              defectSku: 1,
              defectQty: 7,
              scrapPlannedAt: '2026-08-23T00:00:00',
              rtsPlannedAt: '2026-08-25T09:00:00',
            },
          ],
        },
        pagination: { listView: 50, listTotal: 4, cursor: 0 },
      },
    },
    {
      // 컬리나우 센터 1건 + 일반 센터 1건 — 폐기 신청 버튼 차단 검증용
      name: '컬리나우 센터 포함',
      response: {
        data: {
          issues: [
            {
              inspectionId: 201,
              purchaseOrderCode: 'RO202609010001',
              issuedWarehouse: 'MC01',
              issuedWarehouseName: '컬리나우-DMC점',
              isKurlyNowCenter: true,
              cluster: 'MC01',
              issueRegisteredAt: '2026-09-01T10:00:00',
              elapsedDays: 17,
              inspectionProcessMethod: 'APPLICATION_AWAITING',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566779001',
              goodsName: '컬리나우 케이스 - 전통주 막걸리 750ml',
              issueType: '파손',
              defectSku: 1,
              defectQty: 6,
              scrapPlannedAt: '2026-09-16T00:00:00',
              rtsPlannedAt: null,
            },
            {
              inspectionId: 202,
              purchaseOrderCode: 'RO202609010001',
              issuedWarehouse: 'GPM1',
              issuedWarehouseName: '김포상온',
              isKurlyNowCenter: false,
              cluster: 'CC01',
              issueRegisteredAt: '2026-09-01T10:00:00',
              elapsedDays: 17,
              inspectionProcessMethod: 'APPLICATION_AWAITING',
              inspectionProcessStatus: 'PENDING_INPUT',
              goodsCode: 'HB5566779002',
              goodsName: '일반 센터 케이스 - 냉동 만두 1kg',
              issueType: '파손',
              defectSku: 1,
              defectQty: 4,
              scrapPlannedAt: '2026-09-16T00:00:00',
              rtsPlannedAt: null,
            },
          ],
        },
        pagination: { listView: 50, listTotal: 2, cursor: 0 },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: { issues: [] },
        pagination: { listView: 50, listTotal: 0, cursor: 0 },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '목록을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
